import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import Carousel from '@comp/Carousel/Carousel';

const shimmerAnimation = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const ShimmerBox = styled.div<{
  width?: string;
  height?: string;
  borderRadius?: string;
}>`
  width: ${(props) => props.width || '100%'};
  height: ${(props) => props.height || '100%'};
  border-radius: ${(props) => props.borderRadius || '8px'};
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0.15) 50%,
    rgba(255, 255, 255, 0.05) 100%
  );
  background-size: 200% 100%;
  animation: ${shimmerAnimation} 1s linear infinite;
`;

const PageShimmerWrapper = styled.div`
  width: 100%;
  grid-column: 1 / -1;
  min-height: 100vh;
  box-sizing: border-box;
  overflow-x: hidden;

  & > div {
    width: 100%;
    box-sizing: border-box;
  }

  /* Hide carousel controls in shimmer */
  button[class*='ControlButton'],
  button[class*='StyledIconButton'] {
    display: none !important;
  }
`;

const SubHeaderShimmer = styled.div`
  width: 100%;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--color-i-prime-surface-dark, #1a1a2e);
`;

const TopRowShimmer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CardsGridShimmer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  padding: 24px 16px;
  width: 100%;
  box-sizing: border-box;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const CardShimmerWrap = styled.div`
  width: 100%;
  aspect-ratio: 16 / 20;

  @media screen and (max-width: 768px) {
    aspect-ratio: auto;
    height: 120px;
  }
`;

const CardShimmerContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media screen and (max-width: 768px) {
    flex-direction: row;
  }
`;

const CardThumbnailShimmer = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 8px;
  overflow: hidden;

  @media screen and (max-width: 768px) {
    width: 160px;
    height: 100%;
    aspect-ratio: auto;
  }
`;

const CardMetadataShimmer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;

  @media screen and (max-width: 768px) {
    justify-content: center;
  }
`;

export const PrimePageSubHeaderShimmer = ({
  showEducatorPill = false
}: {
  showEducatorPill?: boolean;
}) => {
  return (
    <SubHeaderShimmer>
      <TopRowShimmer>
        <ShimmerBox width="24px" height="24px" borderRadius="4px" />
        <ShimmerBox width="150px" height="24px" borderRadius="4px" />
      </TopRowShimmer>
      {showEducatorPill && (
        <ShimmerBox width="120px" height="32px" borderRadius="16px" />
      )}
    </SubHeaderShimmer>
  );
};

export const PrimeCardShimmer = ({
  isMobile = false
}: {
  isMobile?: boolean;
}) => {
  return (
    <CardShimmerWrap>
      <CardShimmerContent>
        <CardThumbnailShimmer>
          <ShimmerBox width="100%" height="100%" borderRadius="8px" />
        </CardThumbnailShimmer>
        <CardMetadataShimmer>
          <ShimmerBox width="90%" height="16px" borderRadius="4px" />
          <ShimmerBox width="60%" height="14px" borderRadius="4px" />
        </CardMetadataShimmer>
      </CardShimmerContent>
    </CardShimmerWrap>
  );
};

export const PrimeCardsGridShimmer = ({
  numberOfCards = 12,
  isMobile = false
}: {
  numberOfCards?: number;
  isMobile?: boolean;
}) => {
  return (
    <CardsGridShimmer>
      {Array.from({ length: numberOfCards }).map((_, index) => (
        <PrimeCardShimmer key={index} isMobile={isMobile} />
      ))}
    </CardsGridShimmer>
  );
};

export const PrimePageShimmer = ({
  showEducatorPill = false,
  numberOfCards = 12,
  isMobile = false
}: {
  showEducatorPill?: boolean;
  numberOfCards?: number;
  isMobile?: boolean;
}) => {
  return (
    <PageShimmerWrapper>
      <PrimePageSubHeaderShimmer showEducatorPill={showEducatorPill} />
      <PrimeCardsGridShimmer
        numberOfCards={numberOfCards}
        isMobile={isMobile}
      />
    </PageShimmerWrapper>
  );
};

const SpecialClassesHeroWrapper = styled.div`
  width: 100%;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 16px;
  background: linear-gradient(
    135deg,
    rgba(138, 35, 255, 0.1) 0%,
    rgba(75, 0, 130, 0.1) 100%
  );
  min-height: 100px;
`;

const SpecialClassCardShimmerWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SpecialClassCardShimmer = () => {
  return (
    <SpecialClassCardShimmerWrap>
      <ShimmerBox width="100%" height="120px" borderRadius="8px" />
      <ShimmerBox width="90%" height="14px" borderRadius="4px" />
      <ShimmerBox width="60%" height="12px" borderRadius="4px" />
    </SpecialClassCardShimmerWrap>
  );
};

export const SpecialClassesCarouselSectionShimmer = ({
  label = ''
}: {
  label?: string;
}) => {
  return (
    <Carousel
      isPrime
      label={label}
      numberOfItems={3}
      mobileItemMinWidth="200px"
      showBottomGap={false}
      hideControls
    >
      {Array.from({ length: 3 }).map((_, index) => (
        <SpecialClassCardShimmer key={index} />
      ))}
    </Carousel>
  );
};

export const SpecialClassesPageShimmer = ({
  isMobile = false
}: {
  isMobile?: boolean;
}) => {
  return (
    <PageShimmerWrapper>
      {/* Hero Header */}
      <SpecialClassesHeroWrapper>
        <ShimmerBox width="24px" height="24px" borderRadius="4px" />
        <ShimmerBox width="150px" height="24px" borderRadius="4px" />
        <div style={{ marginLeft: 'auto' }}>
          <ShimmerBox width="200px" height="80px" borderRadius="8px" />
        </div>
      </SpecialClassesHeroWrapper>

      {/* Carousel sections - using actual Carousel component */}
      <SpecialClassesCarouselSectionShimmer label="Live classes" />
      <SpecialClassesCarouselSectionShimmer label="Recent live classes" />
      <SpecialClassesCarouselSectionShimmer label="Coming next" />
      <SpecialClassesCarouselSectionShimmer label="Best of all time" />
    </PageShimmerWrapper>
  );
};
